import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Modal from './ui/Modal';
import Toggle from './ui/Toggle';
import { MobileUserService } from '../api/generated/services/MobileUserService';
import { ApiError } from '../api/generated/core/ApiError';
import { addNotification } from '../store/slices/notificationSlice';
import { helper } from '../utils/helper';
import { usePermissions } from '../hooks/usePermissions';
import type { ShopView } from '../api/generated/models/ShopView';
import type { ShopRequest } from '../api/generated/models/ShopRequest';
import type { AppDispatch } from '../store/store';

interface ShopSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  shop: ShopView;
  onSaved: (updatedShop: ShopView) => void;
  isSubuser: boolean;
  minSyncInterval: number;
}

const SYNC_INTERVAL_OPTIONS = [
  { labelKey: 'shop_settings.interval_0m', value: 0 },
  { labelKey: 'shop_settings.interval_5m', value: 300 },
  { labelKey: 'shop_settings.interval_10m', value: 600 },
];

const DEFAULT_SYNC_INTERVAL = 600;

const MOBILE_BUY_PATH = '/licenses/buy?plugin=mobile';

type ReportFrequency = 'daily' | 'weekly' | 'monthly';

const DEFAULT_REPORT_TIME = '09:30';

function getNotificationValue(
  notifications: ShopView['notifications'],
  type: string,
): boolean | number | string | undefined {
  return notifications?.find((n) => n.notification_type === type)?.value;
}

function getReportToggleEnabled(
  notifications: ShopView['notifications'],
  frequency: ReportFrequency,
): boolean {
  const key = `report_${frequency}`;
  const value = getNotificationValue(notifications, key);
  if (value === undefined) {
    return true;
  }
  return value === true || value === 'true';
}

function getReportTime(
  notifications: ShopView['notifications'],
  frequency: ReportFrequency,
): string {
  const key = `report_${frequency}_time`;
  const value = getNotificationValue(notifications, key);
  if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
    return value;
  }
  return DEFAULT_REPORT_TIME;
}

const ShopSettingsModal: React.FC<ShopSettingsModalProps> = ({
  isOpen,
  onClose,
  shop,
  onSaved,
  isSubuser,
  minSyncInterval,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // Subuser: PUT /mobile/user/shop-update requires mobile_write.
  const { can } = usePermissions();
  const canWrite = can('mobile_write');

  const [newOrder, setNewOrder] = useState(false);
  const [siteDown, setSiteDown] = useState(false);
  const [siteUp, setSiteUp] = useState(false);
  const [syncInterval, setSyncInterval] = useState(DEFAULT_SYNC_INTERVAL);
  const [reportDaily, setReportDaily] = useState(true);
  const [reportWeekly, setReportWeekly] = useState(true);
  const [reportMonthly, setReportMonthly] = useState(true);
  const [reportDailyTime, setReportDailyTime] = useState(DEFAULT_REPORT_TIME);
  const [reportWeeklyTime, setReportWeeklyTime] = useState(DEFAULT_REPORT_TIME);
  const [reportMonthlyTime, setReportMonthlyTime] = useState(DEFAULT_REPORT_TIME);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const initFromShop = useCallback((s: ShopView) => {
    setNewOrder(getNotificationValue(s.notifications, 'new_order') === true);
    setSiteDown(getNotificationValue(s.notifications, 'site_down') === true);
    setSiteUp(getNotificationValue(s.notifications, 'site_up') === true);

    const syncVal = getNotificationValue(s.notifications, 'sync_interval');
    // 0 is a valid value (Instant), so only fall back when the value is missing.
    const interval =
      typeof syncVal === 'number' && syncVal >= 0
        ? syncVal
        : DEFAULT_SYNC_INTERVAL;
    setSyncInterval(interval);

    setReportDaily(getReportToggleEnabled(s.notifications, 'daily'));
    setReportWeekly(getReportToggleEnabled(s.notifications, 'weekly'));
    setReportMonthly(getReportToggleEnabled(s.notifications, 'monthly'));
    setReportDailyTime(getReportTime(s.notifications, 'daily'));
    setReportWeeklyTime(getReportTime(s.notifications, 'weekly'));
    setReportMonthlyTime(getReportTime(s.notifications, 'monthly'));
  }, []);

  useEffect(() => {
    if (isOpen) {
      initFromShop(shop);
      setHydrated(true);
    } else {
      setHydrated(false);
    }
  }, [isOpen, shop, initFromShop]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const notifications: ShopRequest['notifications'] = [
        { notification_type: 'new_order', value: newOrder },
        { notification_type: 'site_down', value: siteDown },
        { notification_type: 'site_up', value: siteUp },
        { notification_type: 'sync_interval', value: syncInterval },
        { notification_type: 'report_daily', value: reportDaily },
        { notification_type: 'report_daily_time', value: reportDailyTime },
        { notification_type: 'report_weekly', value: reportWeekly },
        { notification_type: 'report_weekly_time', value: reportWeeklyTime },
        { notification_type: 'report_monthly', value: reportMonthly },
        { notification_type: 'report_monthly_time', value: reportMonthlyTime },
      ];

      const request: ShopRequest = {
        shopId: shop.shopId,
        host: shop.host,
        shopType: shop.shopType as unknown as ShopRequest.shopType,
        notifications,
      };

      if (!isSubuser) {
        request.isEnabled = shop.isEnabled;
        request.isOnline = shop.isOnline;
        request.settings = shop.settings;
      }

      await MobileUserService.putAppMobileUserShopUpdate(request);

      const updatedShop = await MobileUserService.getAppMobileUserShopGet(
        shop.shopId,
      );
      onSaved(updatedShop);

      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('shop_settings.saved_success'),
        }),
      );
      onClose();
    } catch (err) {
      let errorMessage = t('shop_settings.save_error');
      if (err instanceof ApiError) {
        const parsed = helper.parseApiErrorBody(err);
        if (parsed?.error) {
          errorMessage = parsed.error;
        }
      }
      dispatch(
        addNotification({
          type: 'error',
          title: t('notification_error'),
          message: errorMessage,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const shopLabel = (() => {
    if (!shop.host) return shop.shopId;
    const h = shop.host.trim();
    if (h.startsWith('http://') || h.startsWith('https://')) {
      try {
        return new URL(h).hostname || h;
      } catch {
        return h;
      }
    }
    return h;
  })();

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={shopLabel}
        className="shop-settings-modal"
      >
        <div className="shop-settings-modal__content">
          <h4 className="shop-settings-modal__section-title">
            {t('shop_settings.title')}
          </h4>

          <div className="shop-settings-modal__row">
            <div className="shop-settings-modal__row-info">
              <span className="shop-settings-modal__row-label">
                {t('shop_settings.new_order')}
              </span>
              <span className="shop-settings-modal__row-hint">
                {t('shop_settings.new_order_hint')}
              </span>
            </div>
            <Toggle checked={newOrder} onChange={setNewOrder} />
          </div>

          <div className="shop-settings-modal__row">
            <div className="shop-settings-modal__row-info">
              <span className="shop-settings-modal__row-label">
                {t('shop_settings.site_down')}
              </span>
              <span className="shop-settings-modal__row-hint">
                {t('shop_settings.site_down_hint')}
              </span>
            </div>
            <Toggle checked={siteDown} onChange={setSiteDown} />
          </div>

          <div className="shop-settings-modal__row">
            <div className="shop-settings-modal__row-info">
              <span className="shop-settings-modal__row-label">
                {t('shop_settings.site_up')}
              </span>
              <span className="shop-settings-modal__row-hint">
                {t('shop_settings.site_up_hint')}
              </span>
            </div>
            <Toggle checked={siteUp} onChange={setSiteUp} />
          </div>

          <div className="shop-settings-modal__row">
            <div className="shop-settings-modal__row-info">
              <span className="shop-settings-modal__row-label">
                {t('shop_settings.sync_interval')}
              </span>
              <span className="shop-settings-modal__row-hint">
                {t('shop_settings.sync_interval_hint')}
              </span>
            </div>
            <div className="shop-settings-modal__interval-buttons">
              {SYNC_INTERVAL_OPTIONS.map((opt) => {
                const isLocked =
                  minSyncInterval > 0 && opt.value < minSyncInterval;
                const isActive = syncInterval === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`shop-settings-modal__interval-btn ${isActive ? 'shop-settings-modal__interval-btn--active' : ''}`}
                    onClick={() =>
                      isLocked
                        ? setUpgradeModalOpen(true)
                        : setSyncInterval(opt.value)
                    }
                  >
                    {t(opt.labelKey)}
                    {isLocked && (
                      <span className="content-master-pro-badge">
                        {t('shop_settings.upgrade_badge')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <h4 className="shop-settings-modal__section-title shop-settings-modal__section-title--reports">
            {t('shop_reports.title')}
          </h4>
          <p className="shop-settings-modal__reports-hint">{t('shop_reports.timezone_hint')}</p>

          {(
            [
              {
                freq: 'daily' as ReportFrequency,
                enabled: reportDaily,
                setEnabled: setReportDaily,
                time: reportDailyTime,
                setTime: setReportDailyTime,
                labelKey: 'shop_reports.daily',
              },
              {
                freq: 'weekly' as ReportFrequency,
                enabled: reportWeekly,
                setEnabled: setReportWeekly,
                time: reportWeeklyTime,
                setTime: setReportWeeklyTime,
                labelKey: 'shop_reports.weekly',
              },
              {
                freq: 'monthly' as ReportFrequency,
                enabled: reportMonthly,
                setEnabled: setReportMonthly,
                time: reportMonthlyTime,
                setTime: setReportMonthlyTime,
                labelKey: 'shop_reports.monthly',
              },
            ] as const
          ).map((row) => (
            <div key={row.freq} className="shop-settings-modal__row shop-settings-modal__row--report">
              <div className="shop-settings-modal__row-info">
                <span className="shop-settings-modal__row-label">{t(row.labelKey)}</span>
              </div>
              <Toggle checked={row.enabled} onChange={row.setEnabled} />
              <label className="shop-settings-modal__time-field">
                <span className="shop-settings-modal__time-label">{t('shop_reports.time')}</span>
                <input
                  type="time"
                  className="shop-settings-modal__time-input"
                  value={row.time}
                  onChange={(event) => row.setTime(event.target.value)}
                  disabled={!row.enabled}
                />
              </label>
            </div>
          ))}

          <div className="shop-settings-modal__actions">
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={saving || !canWrite || !hydrated}
              title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
            >
              {saving ? t('shop_settings.saving') : t('shop_settings.save')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        title={t('shop_settings.upgrade_modal_title')}
        isCentered
        hideCloseButton
        className="content-master-confirm-modal content-master-confirm-modal--subscription"
      >
        <div className="content-master-confirm-modal__layout">
          <div className="content-master-confirm-modal__text">
            <p>{t('shop_settings.upgrade_modal_body')}</p>
          </div>
          <div
            className="content-master-confirm-modal__illustration"
            aria-hidden
          />
        </div>
        <div className="content-master-confirm-modal__buttons">
          <button
            type="button"
            className="btn btn-secondary content-master-confirm-modal__btn-secondary"
            onClick={() => setUpgradeModalOpen(false)}
          >
            {t('cancelText')}
          </button>
          <button
            type="button"
            className="btn btn-primary content-master-confirm-modal__btn-primary"
            onClick={() => {
              setUpgradeModalOpen(false);
              navigate(MOBILE_BUY_PATH);
            }}
          >
            {t('widget_upgrade_cta_button')}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ShopSettingsModal;
